import React from 'react'
import classes from './EmailLoginForm.module.sass'
import { Link, useNavigate } from 'react-router-dom'
import {
  PERSONAL_DATA_AGREEMENT_ROUTE,
  PRIVACY_POLICY_ROUTE,
  USER_AGREEMENT_ROUTE,
  USER_ROUTE,
} from '../../../common/utils/constants'
import { EmailStage } from '../../../common/types/enums'
import { Loader } from '../../../common/components/loader/Loader'
import { Button } from '../../../common/components/button/Button'
import { Toggle } from '../../../common/components/toggle/Toggle'
import { Input } from '../../../common/components/input/Input'
import { useEmailLoginForm } from './useEmailLoginForm'

type Props = {
  setModalActive: (state: boolean) => void
  attach?: boolean
  initialEmail?: string
  onAttachComplete?: () => void // ← добавит
}

export const EmailLoginForm = ({
  setModalActive,
  attach,
  initialEmail,
  onAttachComplete,
}: Props) => {
  const {
    inputHandler,
    data,
    getValidationResult,
    setDirty,
    stage,
    loading,
    message,
    registerMessage,
    isLogin,
    legalityChecked,
    timer,
    requestCode,
    verifyCode,
    register,
    resendCode,
    toggleLegality,
    disabled,
    checkDisabled,
    setStage,
    setData
  } = useEmailLoginForm(setModalActive)
  const navigate = useNavigate()

  const checkBoxTitle = (
    <>
      {`Согласие с `}
      <Link target="blank" to={PRIVACY_POLICY_ROUTE}>
        политикой в отношении обработки персональных данных
      </Link>
      {`, `}
      <Link target="blank" to={PERSONAL_DATA_AGREEMENT_ROUTE}>
        соглашением об обработке персональных данных
      </Link>
      {` и `}
      <Link target="blank" to={USER_AGREEMENT_ROUTE}>
        пользовательским соглашением
      </Link>
    </>
  )

  return (
    <div className={classes.Container} onClick={(e) => e.stopPropagation()}>
      {loading ? (
        <>
          <Loader />
          <div className={classes.Message}>{message}</div>
        </>
      ) : (
        <>
          <div className={classes.Title}>
            {attach ? 'Привязка email' : isLogin ? 'Вход по email' : 'Регистрация'}
          </div>

          {stage === EmailStage.EMAIL_INPUT && (
            <>
              <div className={classes.Description}>
                {attach
                  ? 'Для повышения безопасности аккаунта и возможности восстановления доступа укажите ваш email. На него будет отправлен код подтверждения'
                  : 'Для стабильного доступа к системе используйте email. После ввода email вы получите код подтверждения для входа или регистрации'}
              </div>

              <Input
                type="text"
                value={data.email}
                placeHolder="example@mail.ru"
                onChangeHandler={inputHandler}
                fieldName="email"
                itemValidationResult={getValidationResult('email')}
                onBlur={setDirty}
              />

              <Button
                text="Получить код"
                onClick={requestCode}
                disabled={disabled}
                timeoutMs={1000}
              />

              {attach && (
                <Button
                  text="Пропустить"
                  onClick={() => {
                    navigate(USER_ROUTE)
                    setModalActive(false)
                  }}
                  timeoutMs={0}
                />
              )}
            </>
          )}

          {stage === EmailStage.CODE_INPUT && (
            <>
              <div className={classes.Description}>
                {registerMessage ||
                  (attach
                    ? `На указанный email ${data.email} отправлен код подтверждения. Введите его ниже для завершения привязки`
                    : `Код подтверждения отправлен на ${data.email}. Если письмо не пришло, проверьте папку Спам или запросите код повторно`)}
              </div>

              <div className={classes.codeContainer}>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <input
                    key={i}
                    className={classes.codeInput}
                    value={data.code[i] || ''}
                    onChange={(e) => {
                      const newCode = data.code.split('')
                      newCode[i] = e.target.value
                      setData({ ...data, code: newCode.join('') })
                      if (e.target.value && i < 5) {
                        document.getElementById(`code-${i + 1}`)?.focus()
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' && !data.code[i] && i > 0) {
                        document.getElementById(`code-${i - 1}`)?.focus()
                      }
                    }}
                    id={`code-${i}`}
                    type="text"
                    maxLength={1}
                    inputMode="numeric"
                  />
                ))}
              </div>

              {message && !registerMessage && <div className={classes.Message}>{message}</div>}

              {isLogin && !registerMessage && (
                <div className={classes.ResendContainer}>
                  <span>Не пришел код? </span>
                  <button
                    onClick={resendCode}
                    disabled={timer > 0}
                    className={classes.ResendButton}
                  >
                    {timer > 0 ? `повтор через ${timer}с` : 'Отправить еще раз'}
                  </button>
                </div>
              )}

              {!isLogin && (
                <>
                  <div className={classes.ToggleContainer}>
                    <Toggle id={'legality'} onChange={toggleLegality} checked={legalityChecked} />
                    <div>{checkBoxTitle}</div>
                  </div>
                  <Button
                    text="Зарегистрироваться"
                    onClick={register}
                    disabled={!legalityChecked || checkDisabled}
                    timeoutMs={1000}
                  />
                </>
              )}

              {isLogin && !registerMessage && (
                <Button
                  text="Войти"
                  onClick={verifyCode}
                  disabled={checkDisabled}
                  timeoutMs={1000}
                />
              )}

              {attach && (
                <Button
                  text="Пропустить"
                  onClick={() => {
                    setModalActive(false)
                    navigate(USER_ROUTE)
                  }}
                  timeoutMs={0}
                />
              )}
            </>
          )}

          {stage === EmailStage.ERROR && (
            <>
              <div className={classes.Title}>Ошибка</div>
              <div className={classes.Message}>
                {message || 'Что-то пошло не так. Пожалуйста, попробуйте еще раз'}
              </div>
              <Button
                text="Вернуться к вводу email"
                onClick={() => setStage(EmailStage.EMAIL_INPUT)}
                timeoutMs={1000}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}
